import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Building2, Plus, Edit2, Trash2, Phone, Mail, MapPin, FileText, LogOut, ArrowLeft, Users, Search, ExternalLink, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

// Liens extranets prédéfinis
const EXTRANETS_PREDEFINIS = [
  { nom: 'Dynaren', url: 'https://extranet.dynaren.fr/index/index' },
  { nom: 'Domus Services', url: 'https://extranet.domus-services.fr/prestataires/#/' },
];

const TYPES_DONNEUR = [
  { value: 'assurance', label: 'Assurance', color: 'bg-blue-100 text-blue-800' },
  { value: 'syndic', label: 'Syndic', color: 'bg-purple-100 text-purple-800' },
  { value: 'particulier', label: 'Particulier', color: 'bg-green-100 text-green-800' },
  { value: 'entreprise', label: 'Entreprise', color: 'bg-orange-100 text-orange-800' },
  { value: 'expert', label: 'Expert', color: 'bg-red-100 text-red-800' },
];

function DonneursOrdre() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [donneurs, setDonneurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDonneur, setEditingDonneur] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    type: 'assurance',
    contact_nom: '',
    contact_email: '',
    contact_telephone: '',
    adresse: '',
    notes: '',
    extranet_url: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchDonneurs();
  }, []);

  const fetchDonneurs = async () => {
    try {
      const res = await axios.get(`${API}/donneurs-ordre`);
      setDonneurs(res.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.nom || !formData.type) {
      toast.error('Nom et type requis');
      return;
    }

    try {
      if (editingDonneur) {
        await axios.put(`${API}/donneurs-ordre/${editingDonneur.id}`, formData);
        toast.success('Donneur d\'ordre mis à jour');
      } else {
        await axios.post(`${API}/donneurs-ordre`, formData);
        toast.success('Donneur d\'ordre créé');
      }
      setShowAddDialog(false);
      setEditingDonneur(null);
      resetForm();
      fetchDonneurs();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce donneur d\'ordre ?')) return;
    try {
      await axios.delete(`${API}/donneurs-ordre/${id}`);
      toast.success('Supprimé');
      fetchDonneurs();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const openEditDialog = (donneur) => {
    setEditingDonneur(donneur);
    setFormData({
      nom: donneur.nom,
      type: donneur.type,
      contact_nom: donneur.contact_nom || '',
      contact_email: donneur.contact_email || '',
      contact_telephone: donneur.contact_telephone || '',
      adresse: donneur.adresse || '',
      notes: donneur.notes || '',
      extranet_url: donneur.extranet_url || '',
      logo_url: donneur.logo_url || ''
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      type: 'assurance',
      contact_nom: '',
      contact_email: '',
      contact_telephone: '',
      adresse: '',
      notes: '',
      extranet_url: '',
      logo_url: ''
    });
  };

  const getTypeInfo = (type) => TYPES_DONNEUR.find(t => t.value === type) || TYPES_DONNEUR[0];

  const filteredDonneurs = donneurs.filter(d => {
    const matchSearch = d.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (d.contact_nom || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || d.type === filterType;
    return matchSearch && matchType;
  });

  // Stats
  const stats = {
    total: donneurs.length,
    assurances: donneurs.filter(d => d.type === 'assurance').length,
    syndics: donneurs.filter(d => d.type === 'syndic').length,
    autres: donneurs.filter(d => !['assurance', 'syndic'].includes(d.type)).length
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="KARDIF" className="h-12" />
            <div>
              <h1 className="text-xl font-bold text-[#1A1A1A]">Donneurs d'ordre</h1>
              <p className="text-sm text-[#6B7280]">Gestion des mandataires</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => nav('/secretary')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <Button variant="ghost" size="icon" onClick={() => logout()}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Liens extranets rapides */}
        <Card className="mb-6 bg-gradient-to-r from-[#8B7355]/10 to-[#8B7355]/5 border-[#8B7355]/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Globe className="h-6 w-6 text-[#8B7355]" />
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Accès rapide aux extranets</p>
                  <p className="text-sm text-[#6B7280]">Déposez vos documents directement</p>
                </div>
              </div>
              <div className="flex gap-2">
                {EXTRANETS_PREDEFINIS.map((ext, i) => (
                  <a
                    key={i}
                    href={ext.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border hover:border-[#8B7355] hover:shadow-md transition-all"
                  >
                    <ExternalLink className="h-4 w-4 text-[#8B7355]" />
                    <span className="font-medium">{ext.nom}</span>
                  </a>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{stats.total}</p>
                  <p className="text-sm text-[#6B7280]">Total</p>
                </div>
                <Building2 className="h-8 w-8 text-[#8B7355]" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{stats.assurances}</p>
                  <p className="text-sm text-[#6B7280]">Assurances</p>
                </div>
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">{stats.syndics}</p>
                  <p className="text-sm text-[#6B7280]">Syndics</p>
                </div>
                <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.autres}</p>
                  <p className="text-sm text-[#6B7280]">Autres</p>
                </div>
                <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {TYPES_DONNEUR.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingDonneur(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#8B7355] hover:bg-[#7A6548] text-white">
                <Plus className="h-4 w-4 mr-2" />
                Nouveau donneur d'ordre
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingDonneur ? 'Modifier le donneur d\'ordre' : 'Nouveau donneur d\'ordre'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Nom / Raison sociale *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Ex: AXA, Foncia, M. Dupont..."
                  />
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_DONNEUR.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du contact</Label>
                    <Input
                      value={formData.contact_nom}
                      onChange={(e) => setFormData({ ...formData, contact_nom: e.target.value })}
                      placeholder="Prénom Nom"
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      value={formData.contact_telephone}
                      onChange={(e) => setFormData({ ...formData, contact_telephone: e.target.value })}
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@exemple.com"
                  />
                </div>
                <div>
                  <Label>Adresse</Label>
                  <Textarea
                    value={formData.adresse}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    placeholder="Adresse complète"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Informations complémentaires..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Lien extranet (pour déposer les documents)
                  </Label>
                  <Input
                    value={formData.extranet_url}
                    onChange={(e) => setFormData({ ...formData, extranet_url: e.target.value })}
                    placeholder="https://extranet.exemple.com/..."
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Logo (URL) — affiché sur la page de garde du rapport
                  </Label>
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://.../logo-assurance.png"
                    data-testid="donneur-logo-url-input"
                  />
                  {formData.logo_url ? (
                    <div className="mt-2 flex items-center gap-3 p-2 border rounded-lg bg-gray-50">
                      <img
                        src={formData.logo_url}
                        alt="Aperçu logo"
                        className="h-12 max-w-[140px] object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        data-testid="donneur-logo-preview"
                      />
                      <span className="text-xs text-[#6B7280]">Aperçu du logo</span>
                    </div>
                  ) : (
                    <p className="text-xs text-[#9CA3AF] mt-1">Collez l'URL du logo de l'assurance / du donneur d'ordre.</p>
                  )}
                </div>
                <Button onClick={handleSubmit} className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white">
                  {editingDonneur ? 'Enregistrer' : 'Créer'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : filteredDonneurs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="h-12 w-12 text-[#6B7280] mx-auto mb-4" />
              <p className="text-[#6B7280]">Aucun donneur d'ordre trouvé</p>
              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="mt-4 bg-[#8B7355] hover:bg-[#7A6548] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer le premier
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredDonneurs.map(donneur => {
              const typeInfo = getTypeInfo(donneur.type);
              return (
                <Card key={donneur.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {donneur.logo_url ? (
                          <div className="h-12 w-12 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
                            <img
                              src={donneur.logo_url}
                              alt={donneur.nom}
                              className="h-full w-full object-contain p-1"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </div>
                        ) : (
                          <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${typeInfo.color.replace('text-', 'bg-').replace('-800', '-100')}`}>
                            <Building2 className={`h-6 w-6 ${typeInfo.color.split(' ')[1]}`} />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#1A1A1A]">{donneur.nom}</h3>
                            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                          </div>
                          {donneur.contact_nom && (
                            <p className="text-sm text-[#6B7280] mt-1">
                              Contact: {donneur.contact_nom}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#6B7280]">
                            {donneur.contact_telephone && (
                              <a href={`tel:${donneur.contact_telephone}`} className="flex items-center gap-1 hover:text-[#8B7355]">
                                <Phone className="h-3 w-3" />
                                {donneur.contact_telephone}
                              </a>
                            )}
                            {donneur.contact_email && (
                              <a href={`mailto:${donneur.contact_email}`} className="flex items-center gap-1 hover:text-[#8B7355]">
                                <Mail className="h-3 w-3" />
                                {donneur.contact_email}
                              </a>
                            )}
                            {donneur.adresse && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {donneur.adresse.substring(0, 40)}...
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {donneur.extranet_url && (
                          <a
                            href={donneur.extranet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-[#8B7355] text-white rounded-lg hover:bg-[#7A6548] transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Extranet
                          </a>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(donneur)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(donneur.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default DonneursOrdre;
